import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";

const hasValue = (value) => value !== null && value !== undefined && String(value) !== "";

export default function useEventAudienceConfirmation(event) {
  const { t } = useTranslation("events");
  const ageMin = hasValue(event?.target_age_min) ? Number(event.target_age_min) : null;
  const ageMax = hasValue(event?.target_age_max) ? Number(event.target_age_max) : null;
  const hasAge = Number.isFinite(ageMin) || Number.isFinite(ageMax);
  const languageCode = String(event?.event_language || "").trim().toLowerCase();
  const hasLanguage = Boolean(languageCode);
  const viewerAge = hasValue(event?.viewer_age) ? Number(event.viewer_age) : null;
  const ageOutsideRange = hasAge && Number.isFinite(viewerAge) && (
    (Number.isFinite(ageMin) && viewerAge < ageMin) ||
    (Number.isFinite(ageMax) && viewerAge > ageMax)
  );
  const signature = `${event?.id || ""}:${ageMin ?? ""}:${ageMax ?? ""}:${languageCode}:${viewerAge ?? ""}`;
  const acceptedSignatureRef = useRef(null);
  const pendingRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [languageConfirmed, setLanguageConfirmed] = useState(false);

  const finish = useCallback((accepted) => {
    if (accepted) acceptedSignatureRef.current = signature;
    pendingRef.current?.resolve(accepted);
    pendingRef.current = null;
    setOpen(false);
  }, [signature]);

  useEffect(() => () => {
    pendingRef.current?.resolve(false);
    pendingRef.current = null;
  }, []);

  const confirmAudience = useCallback(() => {
    if (!hasAge && !hasLanguage) return Promise.resolve(true);
    if (acceptedSignatureRef.current === signature) return Promise.resolve(true);
    if (pendingRef.current) return pendingRef.current.promise;
    setAgeConfirmed(false);
    setLanguageConfirmed(false);
    setOpen(true);
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    pendingRef.current = { promise, resolve };
    return promise;
  }, [hasAge, hasLanguage, signature]);

  const languageLabel = hasLanguage
    ? t(`event_create.step_advanced.language.options.${languageCode}`, {
        defaultValue: String(event?.event_language_label || "").trim() || languageCode.toUpperCase(),
      })
    : "";
  const ageDescription = Number.isFinite(ageMin) && Number.isFinite(ageMax)
    ? t("join_section.audience.age_range", { min: ageMin, max: ageMax })
    : Number.isFinite(ageMin)
      ? t("join_section.audience.age_min", { min: ageMin })
      : t("join_section.audience.age_max", { max: ageMax });
  const canContinue = !ageOutsideRange && (!hasAge || ageConfirmed) && (!hasLanguage || languageConfirmed);

  const modal = (
    <AppModalShell
      open={open}
      title={t("join_section.audience.title")}
      description={t("join_section.audience.description")}
      onClose={() => finish(false)}
      maxWidthClassName="max-w-md"
      footer={
        <div className="flex gap-3">
          <AppModalButton
            onClick={() => finish(true)}
            disabled={!canContinue}
            className={`flex-1 ${canContinue ? "" : "!bg-white/10 !text-white/40 !shadow-none"}`}
          >
            {t("join_section.audience.continue")}
          </AppModalButton>
          <AppModalButton variant="secondary" onClick={() => finish(false)} className="flex-1">
            {t("join_section.audience.cancel")}
          </AppModalButton>
        </div>
      }
    >
      <div className="space-y-3 text-sm leading-6 text-white/90">
        {hasAge ? (
          <div className="rounded-2xl border border-white/12 bg-white/[0.06] p-4">
            <p>{ageDescription}</p>
            {ageOutsideRange ? (
              <p role="alert" className="mt-2 text-amber-200">
                {t("join_section.audience.age_mismatch", { age: viewerAge })}
              </p>
            ) : (
              <label className="mt-3 flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  className="mt-1 h-5 w-5 accent-blue-500"
                />
                <span>{t("join_section.audience.confirm_age")}</span>
              </label>
            )}
          </div>
        ) : null}
        {hasLanguage ? (
          <div className="rounded-2xl border border-white/12 bg-white/[0.06] p-4">
            <p>{t("join_section.audience.language", { language: languageLabel })}</p>
            <label className="mt-3 flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={languageConfirmed}
                onChange={(e) => setLanguageConfirmed(e.target.checked)}
                className="mt-1 h-5 w-5 accent-blue-500"
              />
              <span>{t("join_section.audience.confirm_language")}</span>
            </label>
          </div>
        ) : null}
      </div>
    </AppModalShell>
  );

  return { confirmAudience, audienceModal: modal };
}
